import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Card, CardContent, Chip, Grid, LinearProgress, Stack, Typography } from '@mui/joy';

interface SystemHealthSummaryProps {
  // Mock data for now, will be replaced with real data later
  totalDrones?: number;
  onlineDrones?: number;
  offlineDrones?: number;
  warningDrones?: number;
}

const SystemHealthSummary = ({
  totalDrones = 12,
  onlineDrones = 8,
  offlineDrones = 2,
  warningDrones = 2
}: SystemHealthSummaryProps) => {
  const { t } = useTranslation();

  // Calculate percentages for the progress bars
  const onlinePercentage = (onlineDrones / totalDrones) * 100;
  const warningPercentage = (warningDrones / totalDrones) * 100;
  const offlinePercentage = (offlineDrones / totalDrones) * 100;

  return (
    <Card variant='outlined'>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level='title-md'>{t('dashboard.system_health', 'System Health Summary')}</Typography>
      </Box>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Stack alignItems='center' direction='row' justifyContent='space-between' mb={1}>
              <Typography level='body-sm'>{t('dashboard.fleet_status', 'Fleet Status')}</Typography>
              <Typography level='body-sm'>
                {onlineDrones} / {totalDrones} {t('dashboard.online', 'online')}
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box
                sx={{ width: `${onlinePercentage}%`, bgcolor: 'success.300', height: 8, borderRadius: '4px 0 0 4px' }}
              />
              <Box sx={{ width: `${warningPercentage}%`, bgcolor: 'warning.300', height: 8 }} />
              <Box
                sx={{ width: `${offlinePercentage}%`, bgcolor: 'danger.300', height: 8, borderRadius: '0 4px 4px 0' }}
              />
            </Box>
          </Box>

          <Grid container spacing={1}>
            <Grid xs={4}>
              <Stack alignItems='center' direction='row' spacing={1}>
                <CheckCircleIcon color='success' fontSize='small' />
                <Box>
                  <Typography color='success' level='body-xs'>
                    {t('dashboard.online', 'Online')}
                  </Typography>
                  <Typography level='body-md'>{onlineDrones}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid xs={4}>
              <Stack alignItems='center' direction='row' spacing={1}>
                <WarningIcon color='warning' fontSize='small' />
                <Box>
                  <Typography color='warning' level='body-xs'>
                    {t('dashboard.warning', 'Warning')}
                  </Typography>
                  <Typography level='body-md'>{warningDrones}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid xs={4}>
              <Stack alignItems='center' direction='row' spacing={1}>
                <ErrorIcon color='error' fontSize='small' />
                <Box>
                  <Typography color='danger' level='body-xs'>
                    {t('dashboard.offline', 'Offline')}
                  </Typography>
                  <Typography level='body-md'>{offlineDrones}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Box>
            <Typography level='body-sm' mb={1}>
              {t('dashboard.system_alerts', 'System Alerts')}
            </Typography>
            <Stack direction='row' flexWrap='wrap' spacing={1}>
              <Chip color='danger' size='sm' variant='soft'>
                2 {t('dashboard.critical', 'Critical')}
              </Chip>
              <Chip color='warning' size='sm' variant='soft'>
                3 {t('dashboard.warnings', 'Warnings')}
              </Chip>
              <Chip color='neutral' size='sm' variant='soft'>
                5 {t('dashboard.notifications', 'Notifications')}
              </Chip>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SystemHealthSummary;
